import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { motion } from "framer-motion";
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { ANIMATION_DURATION } from "@/constants/animations.constants";
import { MobileViewportContainer } from "@/modules/onboarding/components/MobileViewportContainer/MobileViewportContainer";
import { OnboardingBackButton } from "@/modules/onboarding/components/OnboardingBackButton/OnboardingBackButton";
import { VerificationCodeForm } from "@/modules/onboarding/components/VerificationCodeInput/VerificationCodeForm";
import { LocalStorageService } from "@/modules/storage";
import { get, post } from "api";
import { queryClient } from "pages/Root";
import Services from "services/Services";
// Match legacy URL composition used in VerificationCodePage
const getEnvScheme = () =>
  new URL(import.meta.env.REACT_APP_API_BASE_URL ?? location.href).protocol;

type V3VerifyState = {
  phone?: string;
  newUser?: boolean;
  signupReferralCode?: string;
  statelessReferralCode?: string;
};

const V3VerificationCodePage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const stateInfo = useMemo(() => {
    if (location.state) {
      return location.state as V3VerifyState;
    }
    const persisted = LocalStorageService.getItem("v3SignInState");
    if (persisted) {
      return JSON.parse(persisted) as V3VerifyState;
    }
    return {};
  }, [location.state]);

  const onVerificationCodeSubmitted = useCallback(
    async (verificationCode: string) => {
      const { phone, newUser, signupReferralCode, statelessReferralCode } =
        stateInfo;

      // Add delay in development to see the loader
      if (import.meta.env.DEV) {
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }

      try {
        if (Capacitor.isNativePlatform()) {
          // Mirror legacy native verify behavior
          const verify = await post("/multi-platform/auth/verify", {
            phoneNumber: "+" + phone?.replaceAll(/\D/g, ""),
            verificationCode: verificationCode,
          });

          const platformTokens = verify.platformDomainTokens;

          await Preferences.set({
            key: "multiPlatformVerifyToken",
            value: verify.token || "",
          });
          await Preferences.set({
            key: "user",
            value: JSON.stringify(verify.user),
          });
          await Preferences.set({
            key: "platformDomainTokens",
            value: JSON.stringify(platformTokens),
          });
          await Preferences.set({
            key: "phoneNumber",
            value: phone || "",
          });

          LocalStorageService.setItem(
            "iconNames",
            JSON.stringify(
              platformTokens.map((p) => `launcher_${p.platformSubdomain}`)
            )
          );

          // Decide next route on native similar to legacy flow
          if (signupReferralCode) {
            LocalStorageService.setItem("referralCode", signupReferralCode);
          }

          if (statelessReferralCode) {
            LocalStorageService.setItem(
              "statelessReferralCode",
              statelessReferralCode
            );
          }

          const localPlatformId = LocalStorageService.getItem("platformId");

          // If explicitly a new user or no connected platforms, go to onboarding entry
          if (newUser === true || platformTokens.length === 0) {
            if (localPlatformId) {
              navigate(
                `/signin/location?platformId=${localPlatformId}${
                  signupReferralCode
                    ? `&referralCode=${signupReferralCode}`
                    : ""
                }`
              );
              return;
            }
            const referralQuery = signupReferralCode
              ? `?referralCode=${signupReferralCode}`
              : "";
            navigate(`/signin/platform-picker${referralQuery}`);
            return;
          }

          if (localPlatformId) {
            const platformDomainToken = platformTokens.find(
              (p) => p.platformId === localPlatformId
            );

            if (!platformDomainToken) {
              navigate("/signin/platform-picker");
              return;
            }

            LocalStorageService.setItem(
              "baseApiUrl",
              `${getEnvScheme()}//${platformDomainToken.platformDomain}`
            );
            await Services.auth.setToken(platformDomainToken.token);
            navigate("/home?fromSignIn=true");
            return;
          }

          if (platformTokens.length === 1) {
            LocalStorageService.setItem(
              "platformId",
              platformTokens[0].platformId
            );
            LocalStorageService.setItem(
              "baseApiUrl",
              `${getEnvScheme()}//${platformTokens[0].platformDomain}`
            );
            await Services.auth.setToken(platformTokens[0].token);
            navigate("/home?fromSignIn=true");
            return;
          }

          navigate("/change-location");
          return;
        } else {
          // Web behavior
          const resp = await post("/auth/verify", {
            phoneNumber: stateInfo.phone,
            verificationCode: verificationCode,
            statelessReferralCode,
            signupReferralCode,
          });

          if (!resp || ("token" in resp && !resp.token)) {
            throw new Error("Unable to verify code. Please try again.");
          }

          if ("token" in resp && resp.token) {
            await Services.auth.setToken(resp.token);
          }

          // Ensure any listeners see updated auth state
          await queryClient.invalidateQueries({ queryKey: ["/user"] });

          if (newUser === true) {
            // Check if coming from gift card claim flow
            const isGiftCardClaim =
              localStorage.getItem("isGiftCardClaim") === "true";
            const giftCardLocationId = localStorage.getItem("locationId");

            // Check if we're on a platform-specific domain
            const platform = await get("/platform/current");
            if (platform?.id) {
              // If claiming gift card and has location, skip both platform and location picker
              if (isGiftCardClaim && giftCardLocationId) {
                // Set location as confirmed
                localStorage.setItem("locationConfirmed", "true");
                // DON'T clear gift card flags yet - we need them throughout onboarding
                // They'll be cleared after the gift card redeem animation
                // Navigate to name page to continue onboarding
                navigate("/signin/name");
                return;
              }

              // We're on a platform domain - skip platform picker and go to location picker
              const referralParam = signupReferralCode
                ? `&referralCode=${signupReferralCode}`
                : "";
              const statelessReferralParam = statelessReferralCode
                ? `&statelessReferralCode=${statelessReferralCode}`
                : "";
              navigate(
                `/signin/location?platformId=${platform.id}${referralParam}${statelessReferralParam}`
              );
              return;
            }

            navigate("/signin/platform-picker");
          } else {
            // Existing user: ensure platform/location flags are set to avoid redirects
            const isGiftCardClaim =
              localStorage.getItem("isGiftCardClaim") === "true";
            const user = await get("/user");
            const platformId = user?.platform?.id;
            const hasLocation = !!user?.platformLocation?.id;
            if (platformId) {
              LocalStorageService.setItem("platformId", platformId);
            }
            if (hasLocation) {
              LocalStorageService.setItem("locationConfirmed", "true");
            }

            // If claiming gift card, navigate to V3 onboarding flow instead of claim page
            // This allows gift card recipients to complete their profile
            if (isGiftCardClaim) {
              // Don't remove the gift card flags yet - we need them throughout onboarding
              // They'll be removed at the end of the onboarding flow
              navigate("/signin/name");
              return;
            }

            navigate("/home?fromSignIn=true");
          }
          return;
        }
      } catch (err) {
        if (err instanceof Error) {
          throw err;
        }
        throw new Error("Unable to verify code. Please try again.");
      }
    },
    [navigate, stateInfo]
  );

  const onResendConfirmationCode = useCallback(async () => {
    try {
      if (!stateInfo.phone) {
        throw new Error("Missing phone number.");
      }
      await post("/auth/login", { phoneNumber: stateInfo.phone });
    } catch (err) {
      if (err instanceof Error) {
        throw err;
      }
      throw new Error("Unable to resend code. Please try again.");
    }
  }, [stateInfo.phone]);

  return (
    <MobileViewportContainer header={<OnboardingBackButton prev="/signin" />}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: ANIMATION_DURATION, delay: 0.1 }}
      >
        <VerificationCodeForm
          phoneNumber={stateInfo.phone}
          onConfirm={onVerificationCodeSubmitted}
          onResendCode={onResendConfirmationCode}
          resendCooldownSeconds={30}
        />
      </motion.div>
    </MobileViewportContainer>
  );
};

export default V3VerificationCodePage;
